import React from 'react';
import { Box, Card, CardContent, Typography, TextField, Button } from '@mui/material';
import { Image as ImageIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface HeroImageUploadProps {
  heroImage: string;
  thumbnailImage: string;
  onHeroChange: (url: string) => void;
  onThumbnailChange: (url: string) => void;
  disabled?: boolean;
}

/**
 * HeroImageUpload — hero + thumbnail image URL fields with preview.
 * Future: integrate with Media Library for drag-and-drop uploads.
 */
export default function HeroImageUpload({
  heroImage,
  thumbnailImage,
  onHeroChange,
  onThumbnailChange,
  disabled = false,
}: HeroImageUploadProps) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
          Images
        </Typography>

        {/* Hero Image */}
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          Hero Image
        </Typography>
        {heroImage ? (
          <Box sx={{ position: 'relative', mb: 1, mt: 0.5 }}>
            <Box
              component="img"
              src={heroImage}
              alt="Hero"
              sx={{
                width: '100%',
                height: 140,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
            {!disabled && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => onHeroChange('')}
                sx={{ position: 'absolute', top: 4, right: 4, minWidth: 'auto', bgcolor: 'rgba(255,255,255,0.9)' }}
              >
                Remove
              </Button>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              height: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              mt: 0.5,
            }}
          >
            <ImageIcon sx={{ color: 'text.disabled', mr: 1 }} />
            <Typography variant="caption" color="text.disabled">
              No hero image
            </Typography>
          </Box>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Hero image URL"
          value={heroImage}
          onChange={(e) => onHeroChange(e.target.value)}
          disabled={disabled}
          sx={{ mb: 2 }}
        />

        {/* Thumbnail */}
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
          Thumbnail Image
        </Typography>
        {thumbnailImage && (
          <Box sx={{ mt: 0.5, mb: 1 }}>
            <Box
              component="img"
              src={thumbnailImage}
              alt="Thumbnail"
              sx={{
                width: 80,
                height: 60,
                objectFit: 'cover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            />
          </Box>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Thumbnail image URL"
          value={thumbnailImage}
          onChange={(e) => onThumbnailChange(e.target.value)}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}
